import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Toaster, toast } from 'sonner';
import {
  Eye,
  EyeOff,
  ArrowLeft,
  GraduationCap,
  UserCheck,
  Mail,
  Lock,
  BookOpen,
  ChevronDown,
  Shield,
} from 'lucide-react';

const inputBase =
  'w-full bg-slate-900/60 border border-slate-700 text-white placeholder-slate-500 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 transition-all duration-200';

const selectBase =
  'w-full bg-slate-900/60 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 transition-all duration-200 appearance-none cursor-pointer';

export default function AcademicUserRegistration({ registerBtn }) {
  const [formValues, setFormValues] = useState({
    role: '',
    subject: '',
    school: '',
    userEmail: '',
    userPassword: '',
    userConfPassword: '',
  });

  const [schools, setSchools] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfPassword, setShowConfPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const server_url = process.env.REACT_APP_API_URI;

  useEffect(() => {
    async function fetchData() {
      try {
        const [schoolResponse, subjectResponse] = await Promise.all([
          axios.get(`${server_url}/universities`),
          axios.get(`${server_url}/context/get-all-subject`),
        ]);
        setSchools(schoolResponse.data);
        setSubjects(subjectResponse.data);
      } catch (err) {
        console.error(err);
      }
    }
    fetchData();
  }, [server_url]);

  const updateFormValue = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleRegisterFormSubmit = async (e) => {
    e.preventDefault();
    const { userPassword, userConfPassword, userEmail } = formValues;
    const emailSuffix = userEmail.split('@')[1];
    let selectedSchool;

    const getSchoolEmailDomains = () => {
      const school = schools.find((s) => s.domains.includes(emailSuffix));
      if (school) {
        selectedSchool = school.name;
        return school.domains;
      }
      return [];
    };

    const schoolEmailDomains = getSchoolEmailDomains();

    if (userPassword !== userConfPassword) {
      toast.error("Passwords don't match. Please try again.");
      return;
    }

    if (!schoolEmailDomains.includes(emailSuffix)) {
      toast.error(
        'Invalid school email address. Contact us to add it to our list.',
      );
      return;
    }

    const newUser = { ...formValues, school: selectedSchool };
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${server_url}/user/register`, newUser, {
        headers: { 'Content-Type': 'application/json' },
      });

      toast.success(
        res.data.message || 'Registration successful! Redirecting to login...',
      );
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          'Registration failed. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Toaster theme="dark" position="top-right" richColors />
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-100px)] py-12 bg-primary font-montserrat px-4">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className="w-full bg-[#111827] shadow-2xl rounded-3xl overflow-hidden border border-slate-800">
            {/* Card Header */}
            <div className="bg-gradient-to-br from-[#081325] to-[#112140] p-8 text-white relative flex flex-col items-center">
              <button
                type="button"
                onClick={registerBtn}
                className="absolute left-6 top-6 flex items-center text-sm gap-1 text-white/70 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <div className="w-16 h-16 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center mb-4 mt-4">
                <GraduationCap
                  strokeWidth={1.5}
                  className="w-8 h-8 text-blue-400"
                />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-center tracking-tight">
                Academic Registration
              </h1>
              <p className="mt-2 text-blue-200/80 text-center text-sm max-w-xs">
                Students &amp; faculty — verify with your institution email.
              </p>
            </div>

            {/* Card Body */}
            <div className="p-8">
              <form onSubmit={handleRegisterFormSubmit} className="space-y-5">
                {/* Role */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-slate-400" />
                    I'm a <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="role"
                      value={formValues.role}
                      onChange={updateFormValue}
                      required
                      className={selectBase}
                    >
                      <option value="" disabled>
                        --- Select Role ---
                      </option>
                      <option value={2}>Student</option>
                      <option value={3}>Faculty Member</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-slate-500 pointer-events-none" />
                  </div>
                </div>

                {/* Area of Study */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-slate-400" />
                    Area of Study <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="subject"
                      value={formValues.subject}
                      onChange={updateFormValue}
                      required
                      className={selectBase}
                    >
                      <option value="" disabled>
                        --- Select Subject ---
                      </option>
                      {subjects.map((subject) => (
                        <option key={subject._id} value={subject.description}>
                          {subject.description}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-slate-500 pointer-events-none" />
                  </div>
                </div>

                {/* School Email */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    School Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="userEmail"
                    value={formValues.userEmail}
                    onChange={updateFormValue}
                    required
                    placeholder="you@university.edu"
                    className={inputBase}
                    autoComplete="email"
                  />
                  <p className="text-xs text-slate-500">
                    We'll never share your email with anyone else.
                  </p>
                </div>

                {/* Password Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <Lock className="w-4 h-4 text-slate-400" />
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="userPassword"
                        value={formValues.userPassword}
                        onChange={updateFormValue}
                        required
                        placeholder="Password"
                        className={`${inputBase} pr-10`}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((p) => !p)}
                        className="absolute right-3 top-3.5 text-slate-500 hover:text-slate-300 transition-colors"
                      >
                        {showPassword ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-300">
                      Confirm <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showConfPassword ? 'text' : 'password'}
                        name="userConfPassword"
                        value={formValues.userConfPassword}
                        onChange={updateFormValue}
                        required
                        placeholder="Confirm"
                        className={`${inputBase} pr-10`}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfPassword((p) => !p)}
                        className="absolute right-3 top-3.5 text-slate-500 hover:text-slate-300 transition-colors"
                      >
                        {showConfPassword ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Terms */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    required
                    className="mt-0.5 h-4 w-4 rounded border-slate-600 bg-slate-900 text-blue-500 accent-blue-500 focus:ring-blue-500 cursor-pointer shrink-0"
                  />
                  <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors leading-relaxed">
                    I accept the{' '}
                    <span className="text-blue-400 font-medium">
                      Terms of Use
                    </span>{' '}
                    and{' '}
                    <span className="text-blue-400 font-medium">
                      Privacy Policy
                    </span>
                  </span>
                </label>

                {/* Notice */}
                <div className="flex items-start gap-3 bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                  <Shield className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-400 leading-relaxed">
                    We use your school email to verify enrolment. If your email
                    domain isn't recognized, please{' '}
                    <span className="text-blue-400 font-medium">
                      contact us
                    </span>{' '}
                    for support.
                  </p>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 rounded-xl text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition-all font-semibold py-3 shadow-lg shadow-blue-500/20 text-sm"
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        />
                      </svg>
                      Registering...
                    </>
                  ) : (
                    <>
                      <GraduationCap className="w-4 h-4" />
                      Create Account
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
